import { useLayoutEffect, useRef, useState } from 'react';
import type { IconComponent } from './Ic';

export interface SegOption<T extends string> {
  value: T;
  label: string;
  icon?: IconComponent;
}

/** Segmented control with a sliding selection. The highlight is an
 *  aria-hidden duplicate of the list, styled "active" throughout and clipped
 *  to the selected item; animating the clip moves background and label colour
 *  together (see .seg-active in components.css). */
export function Seg<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<SegOption<T>>;
  ariaLabel?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Transitions switch on only after the first measurement, so a deep link or
  // refresh paints the highlight in place instead of sliding in from 0.
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const list = listRef.current;
    if (!root || !list) return;
    const measure = () => {
      const index = options.findIndex((o) => o.value === value);
      const btn = list.children[index] as HTMLElement | undefined;
      if (!btn) {
        root.style.setProperty('--l', '0px');
        root.style.setProperty('--r', '100%');
        return;
      }
      const left = btn.offsetLeft;
      const right = list.offsetWidth - (left + btn.offsetWidth);
      root.style.setProperty('--l', `${left}px`);
      root.style.setProperty('--r', `${right}px`);
    };
    measure();
    // Font load or a label change can resize the items after first paint.
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    return () => ro.disconnect();
  }, [value, options]);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="seg" role="group" aria-label={ariaLabel} ref={rootRef} data-ready={ready}>
      <div className="seg-list" ref={listRef}>
        {options.map((o) => {
          const Icon = o.icon;
          return (
            <button
              key={o.value}
              type="button"
              className={o.value === value ? 'on' : ''}
              aria-pressed={o.value === value}
              onClick={() => onChange(o.value)}
            >
              {Icon && <Icon className="ico" />}
              {o.label}
            </button>
          );
        })}
      </div>
      <div className="seg-list seg-active" aria-hidden="true">
        {options.map((o) => {
          const Icon = o.icon;
          return (
            <span key={o.value} className="seg-item">
              {Icon && <Icon className="ico" />}
              {o.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
